<template>
  <div class="insights-precision">
    <div class="insights-precision__head">
      <div>
        <h2 class="insights-precision__title">Accuracy of proposals</h2>
        <p class="insights-precision__intro">
          For each dimension and confidence level, how often the suggested value matched
          what your team actually posted. Compared at <strong>invoice-line level</strong>:
          one proposal line vs. the matching posted line.
        </p>
        <p class="insights-precision__note">
          Use this to confirm High proposals are safe to accept, and to see whether
          Medium/Low proposals are worth reviewing for the dimensions that matter to you.
          Updated approximately once per day.
        </p>
      </div>

      <div class="insights-precision__periods">
        <q-btn-group outline spread>
          <q-btn
            v-for="opt in PERIOD_OPTIONS"
            :key="opt.value"
            :outline="modelValue !== opt.value"
            color="primary"
            :label="opt.label"
            dense
            @click="emit('update:modelValue', opt.value)"
          />
        </q-btn-group>
      </div>
    </div>

    <div class="insights-precision__filters">
      <span class="insights-precision__filters-label">Filter by confidence level:</span>
      <q-checkbox v-model="confidenceFilter" val="High" label="High" dense />
      <q-checkbox v-model="confidenceFilter" val="Medium" label="Medium" dense />
      <q-checkbox v-model="confidenceFilter" val="Low" label="Low" dense />
    </div>

    <q-skeleton v-if="loading" animation="blink" height="320px" />
    <div v-else-if="!perDimensionRows.length && !combinationRows.length" class="insights-precision__empty">
      <q-icon name="inbox" size="32px" color="blue-grey-2" />
      <span>No accuracy data for this period.</span>
    </div>
    <template v-else>
      <q-table
        v-if="perDimensionRows.length"
        flat
        bordered
        dense
        :rows="perDimensionRows"
        :columns="columns"
        row-key="rowKey"
        hide-pagination
        :rows-per-page-options="[0]"
        class="insights-precision__table"
      >
        <template v-slot:body-cell-precision="props">
          <q-td :props="props">
            <div v-if="props.row.sampleSize <= 10">
              N/A
              <q-tooltip max-width="240px">
                Fewer than 10 lines in this dimension × confidence bucket — too small to give a
                meaningful accuracy number. The number will appear once enough lines accumulate.
              </q-tooltip>
            </div>
            <div v-else>{{ props.row.precision.toFixed(1) }}%</div>
          </q-td>
        </template>
      </q-table>

      <div v-if="combinationRows.length" class="insights-precision__combination">
        <div class="insights-precision__combination-head">
          <q-icon name="grid_view" size="18px" class="insights-precision__combination-icon" />
          <div>
            <div class="insights-precision__combination-title">
              All dimensions correct at once
            </div>
            <div class="insights-precision__combination-sub">
              How often <em>every</em> dimension on the line matched at the same time — a
              stricter measure than any single dimension above. A line scores here only if
              Account <em>and</em> Department <em>and</em> Project <em>and</em> any other
              required dimensions were all right.
            </div>
          </div>
        </div>
        <div class="insights-precision__combination-grid">
          <div
            v-for="row in combinationRows"
            :key="row.rowKey"
            class="insights-precision__combination-cell"
            :class="`insights-precision__combination-cell--${row.confidenceLevel.toLowerCase()}`"
          >
            <div class="insights-precision__combination-cell-label">{{ row.confidenceLevel }} confidence</div>
            <div class="insights-precision__combination-cell-value">
              <template v-if="row.sampleSize <= 10">
                N/A
                <q-tooltip max-width="240px">
                  Fewer than 10 lines at this confidence level — too small to give a
                  meaningful accuracy number.
                </q-tooltip>
              </template>
              <template v-else>{{ row.precision.toFixed(1) }}%</template>
            </div>
            <div class="insights-precision__combination-cell-foot">
              {{ formatNumber(row.sampleSize) }} invoice lines
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { InsightsDimensionPrecision, InsightsPrecisionPeriod, InsightsPrecisionResult } from '../types';

const props = defineProps<{
  data: InsightsPrecisionResult | null;
  loading: boolean;
  modelValue: InsightsPrecisionPeriod;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: InsightsPrecisionPeriod): void;
}>();

const PERIOD_OPTIONS: { value: InsightsPrecisionPeriod; label: string }[] = [
  { value: 'PastMonth', label: 'LAST MONTH' },
  { value: 'Past3Months', label: 'LAST 3 MONTHS' },
  { value: 'Past6Months', label: 'LAST 6 MONTHS' },
  { value: 'PastYear', label: 'LAST YEAR' }
];

const confidenceFilter = ref<string[]>([]);

interface PrecisionRow extends InsightsDimensionPrecision {
  rowKey: string;
}

const allRows = computed<PrecisionRow[]>(() =>
  (props.data?.dimensionPrecisions ?? []).map(r => ({
    ...r,
    rowKey: `${r.dimensionName}-${r.confidenceLevel}`
  }))
);

// "Combination" is not a real dimension — it's the all-dimensions-correct rate, which
// follows different rules (a line counts only when every dimension matches at once).
// Splitting it out of the main table avoids the user reading it as "this is a Combination
// dimension" alongside Account / Department / Project.
const CONFIDENCE_ORDER = ['High', 'Medium', 'Low'] as const;

const perDimensionRows = computed<PrecisionRow[]>(() => {
  const rows = allRows.value.filter(r => r.dimensionName !== 'Combination');
  if (confidenceFilter.value.length === 0) return rows;
  return rows.filter(r => confidenceFilter.value.includes(r.confidenceLevel));
});

const combinationRows = computed<PrecisionRow[]>(() => {
  const rows = allRows.value.filter(r => r.dimensionName === 'Combination');
  const filtered = confidenceFilter.value.length === 0
    ? rows
    : rows.filter(r => confidenceFilter.value.includes(r.confidenceLevel));
  return [...filtered].sort(
    (a, b) =>
      CONFIDENCE_ORDER.indexOf(a.confidenceLevel as typeof CONFIDENCE_ORDER[number]) -
      CONFIDENCE_ORDER.indexOf(b.confidenceLevel as typeof CONFIDENCE_ORDER[number])
  );
});

const formatNumber = (n: number): string => new Intl.NumberFormat().format(n);

const columns = [
  { name: 'dimensionName', label: 'Dimension', field: 'dimensionName', align: 'left' as const },
  { name: 'confidenceLevel', label: 'Confidence', field: 'confidenceLevel', align: 'center' as const },
  { name: 'precision', label: 'Accuracy', field: 'precision', align: 'center' as const },
  { name: 'sampleSize', label: 'Invoice lines', field: 'sampleSize', align: 'center' as const }
];
</script>

<style scoped lang="scss">
.insights-precision {
  &__head {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  &__title {
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 600;
    color: #2c2c2c;
  }

  &__intro {
    margin: 0 0 4px;
    font-size: 13px;
    color: #4c4c4c;
    max-width: 480px;
  }

  &__note {
    margin: 0;
    font-size: 12px;
    color: #8b8b8b;
  }

  &__periods {
    margin-left: auto;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;

    &-label {
      font-size: 12px;
      color: #4c4c4c;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 240px;
    color: #8b8b8b;
    font-size: 13px;
  }

  &__table {
    :deep(thead th) {
      background: #f5f5f5;
      font-size: 11px;
      text-transform: uppercase;
      color: #626262;
    }
  }

  &__combination {
    margin-top: 20px;
    padding: 16px;
    border: 1px solid #cfe4ff;
    background: #f6faff;
    border-radius: 10px;

    &-head {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      margin-bottom: 12px;
    }

    &-icon {
      color: #1a4d80;
      margin-top: 2px;
    }

    &-title {
      font-size: 14px;
      font-weight: 600;
      color: #1a4d80;
    }

    &-sub {
      margin-top: 2px;
      font-size: 12px;
      color: #2c5078;
      line-height: 1.4;
      max-width: 640px;
    }

    &-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
    }

    &-cell {
      padding: 12px;
      border-radius: 8px;
      background: white;
      border: 1px solid #d0e3f7;

      &--high {
        border-left: 4px solid #2e9a52;
      }

      &--medium {
        border-left: 4px solid #d39000;
      }

      &--low {
        border-left: 4px solid #c44545;
      }

      &-label {
        font-size: 12px;
        font-weight: 600;
        color: #4c4c4c;
      }

      &-value {
        margin-top: 4px;
        font-size: 22px;
        font-weight: 600;
        color: #2c2c2c;
        line-height: 1.2;
      }

      &-foot {
        margin-top: 4px;
        font-size: 11px;
        color: #8b8b8b;
      }
    }
  }
}
</style>
